import { Controller } from "@hotwired/stimulus"
import { PageFlip as StPageFlip } from "page-flip"
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs"

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.mjs"

export default class extends Controller {
  static targets = ["book", "counter", "fallback", "loading", "soundIcon", "autoplayIcon", "panel", "searchInput", "searchPanel", "progressRing", "progressText"]

  connect() {
    this.pageFlip = null
    this.zoomLevel = 1
    this.isConnected = true
    this.fallbackPage = 1
    this.soundEnabled = true
    this.autoplayTimer = null
    this.flipAudio = null
    this.pageImages = []
    this.activePanelType = null
    this.pdfTextPages = []

    const pdfUrl = this.pdfUrl()
    if (!pdfUrl || !this.hasBookTarget) return

    this.showLoading()

    this.bindNativeArrowHandlers()

    this.initializeFromPdf(pdfUrl)
  }

  disconnect() {
    this.isConnected = false
    this.unbindNativeArrowHandlers()
    if (this.pageFlip) {
      try {
        this.pageFlip.destroy?.()
      } catch (_) {
      }
    }
    this.pageFlip = null
    this.stopAutoplay()
    this.flipAudio = null
  }

  async initializeFromPdf(pdfUrl) {
    try {
      const pdf = await this.loadPdf(pdfUrl)
      if (!this.isConnected || pdf.numPages === 0) {
        this.showFallback()
        return
      }

      // Render ALL pages behind the loading screen with progress
      const allImages = await this.renderPages(pdf, 1, pdf.numPages, pdf.numPages)
      if (!this.isConnected || allImages.length === 0) {
        this.showFallback()
        return
      }

      this.pageImages = allImages
      this.bookReady = false
      this.initializeBook(allImages)
      this.showBook()
      this.updateCounter()
      setTimeout(() => { this.bookReady = true }, 1000)

      // Extract text from all pages for search
      this.extractTextFromPdf(pdf)
    } catch (error) {
      console.error('[Flipbook] Failed to initialize:', error)
      this.showFallback()
    }
  }

  bindNativeArrowHandlers() {
    this.nativePrevButtons = Array.from(this.element.querySelectorAll("[data-action*='flipbook#prev']"))
    this.nativeNextButtons = Array.from(this.element.querySelectorAll("[data-action*='flipbook#next']"))
    this.boundPrevHandler = (event) => this.prev(event)
    this.boundNextHandler = (event) => this.next(event)

    this.nativePrevButtons.forEach((button) => button.addEventListener("click", this.boundPrevHandler))
    this.nativeNextButtons.forEach((button) => button.addEventListener("click", this.boundNextHandler))
  }

  unbindNativeArrowHandlers() {
    if (!this.boundPrevHandler || !this.boundNextHandler) return
    this.nativePrevButtons?.forEach((button) => button.removeEventListener("click", this.boundPrevHandler))
    this.nativeNextButtons?.forEach((button) => button.removeEventListener("click", this.boundNextHandler))
    this.nativePrevButtons = []
    this.nativeNextButtons = []
    this.boundPrevHandler = null
    this.boundNextHandler = null
  }

  prev(event) {
    if (event?.__flipbookHandled) return
    if (event) event.__flipbookHandled = true
    event?.preventDefault()
    if (this.pageFlip) {
      this.pageFlip.flipPrev("top")
      return
    }
    this.flipFallbackPage(-1)
  }

  next(event) {
    if (event?.__flipbookHandled) return
    if (event) event.__flipbookHandled = true
    event?.preventDefault()
    if (this.pageFlip) {
      this.pageFlip.flipNext("top")
      return
    }
    this.flipFallbackPage(1)
  }

  firstPage(event) {
    event?.preventDefault()
    if (this.pageFlip) {
      this.pageFlip.turnToPage(0)
      this.playFlipSound()
      this.updateCounter()
    }
  }

  lastPage(event) {
    event?.preventDefault()
    if (this.pageFlip) {
      this.pageFlip.turnToPage(this.pageFlip.getPageCount() - 1)
      this.playFlipSound()
      this.updateCounter()
    }
  }

  showThumbnails() {
    if (this.activePanelType === 'thumbnails') { this.closePanel(); return }
    this.closePanel()
    if (!this.hasPanelTarget || this.pageImages.length === 0) return

    this.activePanelType = 'thumbnails'
    const panel = this.panelTarget
    panel.innerHTML = ''
    panel.classList.remove('hidden')

    const header = document.createElement('div')
    header.className = 'flex items-center justify-between px-3 py-2 border-b border-white/20'
    header.innerHTML = '<span class="text-sm font-medium text-white">Thumbnails</span><button class="text-white/60 hover:text-white text-lg leading-none" data-action="click->flipbook#closePanel">&times;</button>'
    panel.appendChild(header)

    const grid = document.createElement('div')
    grid.className = 'grid grid-cols-3 gap-2 p-3 overflow-y-auto'
    grid.style.maxHeight = '600px'

    this.pageImages.forEach((src, i) => {
      const thumb = document.createElement('div')
      thumb.className = 'cursor-pointer border-2 border-transparent hover:border-white/60 rounded overflow-hidden'
      const currentIdx = this.pageFlip ? this.pageFlip.getCurrentPageIndex() : 0
      if (i === currentIdx) thumb.className += ' border-white'
      thumb.innerHTML = `<img src="${src}" class="w-full h-auto" /><div class="text-center text-xs text-white/70 py-0.5">${i + 1}</div>`
      thumb.addEventListener('click', () => {
        if (this.pageFlip) {
          this.pageFlip.turnToPage(i)
          this.playFlipSound()
          this.updateCounter()
        }
        this.closePanel()
      })
      grid.appendChild(thumb)
    })

    panel.appendChild(grid)
  }

  showPageList() {
    if (this.activePanelType === 'pagelist') { this.closePanel(); return }
    this.closePanel()
    if (!this.hasPanelTarget || !this.pageFlip) return

    this.activePanelType = 'pagelist'
    const panel = this.panelTarget
    panel.innerHTML = ''
    panel.classList.remove('hidden')

    const header = document.createElement('div')
    header.className = 'flex items-center justify-between px-3 py-2 border-b border-white/20'
    header.innerHTML = '<span class="text-sm font-medium text-white">Pages</span><button class="text-white/60 hover:text-white text-lg leading-none" data-action="click->flipbook#closePanel">&times;</button>'
    panel.appendChild(header)

    const list = document.createElement('div')
    list.className = 'overflow-y-auto p-2'
    list.style.maxHeight = '600px'

    const total = this.pageFlip.getPageCount()
    const currentIdx = this.pageFlip.getCurrentPageIndex()
    for (let i = 0; i < total; i++) {
      const item = document.createElement('div')
      const active = i === currentIdx ? ' bg-white/20 font-medium' : ''
      item.className = `cursor-pointer px-3 py-1.5 text-sm text-white/80 hover:bg-white/15 rounded${active}`
      item.textContent = `Page ${i + 1}`
      item.addEventListener('click', () => {
        if (this.pageFlip) {
          this.pageFlip.turnToPage(i)
          this.playFlipSound()
          this.updateCounter()
        }
        this.closePanel()
      })
      list.appendChild(item)
    }

    panel.appendChild(list)
  }

  async extractTextFromPdf(pdf) {
    this.pdfTextPages = []
    for (let i = 1; i <= pdf.numPages; i++) {
      try {
        const page = await pdf.getPage(i)
        const content = await page.getTextContent()
        const text = content.items.map(item => item.str).join(' ')
        this.pdfTextPages.push(text)
      } catch (_) {
        this.pdfTextPages.push('')
      }
    }
  }

  search(event) {
    // If called from top bar input — open search panel
    if (event?.type === 'keyup' && event.key !== 'Enter') return
    this.openSearchPanel()
  }

  openSearchPanel() {
    if (!this.hasSearchPanelTarget) return
    this.closePanel()

    const panel = this.searchPanelTarget
    panel.innerHTML = ''
    panel.classList.remove('hidden')

    // Header: search icon + title + close
    const header = document.createElement('div')
    header.className = 'flex items-center gap-2 border-b border-gray-200 px-4 py-3'
    header.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" /></svg>
      <span class="text-base font-semibold text-gray-800">Search</span>
      <button class="ml-auto text-gray-400 hover:text-gray-700 text-xl leading-none" data-action="click->flipbook#closeSearchPanel">&times;</button>
    `
    panel.appendChild(header)

    // Search input inside panel
    const inputWrap = document.createElement('div')
    inputWrap.className = 'flex items-center border-b border-gray-200 px-4 py-2'
    const input = document.createElement('input')
    input.type = 'text'
    input.placeholder = 'Search...'
    input.className = 'w-full rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 outline-none focus:border-blue-400'
    input.value = this.hasSearchInputTarget ? this.searchInputTarget.value : ''
    const searchBtn = document.createElement('button')
    searchBtn.className = 'ml-2 shrink-0 text-gray-400 hover:text-gray-700'
    searchBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" /></svg>'

    const doSearch = () => this.executeSearch(input.value, panel)
    input.addEventListener('keyup', (e) => { if (e.key === 'Enter') doSearch() })
    searchBtn.addEventListener('click', doSearch)

    inputWrap.appendChild(input)
    inputWrap.appendChild(searchBtn)
    panel.appendChild(inputWrap)

    // Results container
    const results = document.createElement('div')
    results.id = 'flipbook-search-results'
    results.className = 'overflow-y-auto'
    results.style.maxHeight = '650px'
    panel.appendChild(results)

    // Auto-focus and auto-search if there's a query
    setTimeout(() => input.focus(), 100)
    if (input.value.trim().length >= 2) doSearch()
  }

  executeSearch(query, panel) {
    query = query.trim().toLowerCase()
    const results = panel.querySelector('#flipbook-search-results')
    if (!results) return
    results.innerHTML = ''

    if (query.length < 2) return

    // Sync top bar input
    if (this.hasSearchInputTarget) this.searchInputTarget.value = query

    const matches = []
    this.pdfTextPages.forEach((text, i) => {
      if (text.toLowerCase().includes(query)) {
        const idx = text.toLowerCase().indexOf(query)
        const start = Math.max(0, idx - 60)
        const end = Math.min(text.length, idx + query.length + 60)
        let snippet = text.substring(start, end)
        if (start > 0) snippet = '..' + snippet
        if (end < text.length) snippet = snippet + '..'
        matches.push({ page: i, snippet })
      }
    })

    // Count line
    const countEl = document.createElement('div')
    countEl.className = 'px-4 py-2 text-sm text-gray-600'
    countEl.innerHTML = `Pages: <span class="font-semibold text-blue-600">${matches.length}</span>`
    results.appendChild(countEl)

    if (matches.length === 0) return

    // Highlight helper
    const highlight = (text) => {
      const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
      return text.replace(regex, '<span class="font-bold text-red-600">$1</span>')
    }

    matches.forEach(({ page, snippet }) => {
      const item = document.createElement('div')
      item.className = 'cursor-pointer border-b border-gray-100 px-6 py-3 hover:bg-gray-50'
      item.innerHTML = `
        <div class="text-sm font-bold text-gray-800">Page:${page + 1}</div>
        <div class="mt-1 text-sm leading-relaxed text-gray-600">${highlight(snippet)}</div>
      `
      item.addEventListener('click', () => {
        if (this.pageFlip) {
          this.pageFlip.turnToPage(page)
          this.playFlipSound()
          this.updateCounter()
        }
      })
      results.appendChild(item)
    })
  }

  closeSearchPanel() {
    if (this.hasSearchPanelTarget) {
      this.searchPanelTarget.classList.add('hidden')
      this.searchPanelTarget.innerHTML = ''
    }
  }

  closePanel() {
    this.activePanelType = null
    if (this.hasPanelTarget) {
      this.panelTarget.classList.add('hidden')
      this.panelTarget.innerHTML = ''
    }
    this.closeSearchPanel()
  }

  zoomIn() {
    this.setZoom(this.zoomLevel + 0.1)
  }

  zoomOut() {
    this.setZoom(this.zoomLevel - 0.1)
  }

  toggleSound() {
    this.soundEnabled = !this.soundEnabled
    if (this.hasSoundIconTarget) {
      this.soundIconTarget.innerHTML = this.soundEnabled
        ? '<path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 8.36v7.28a4.47 4.47 0 0 0 2.5-3.64zM14 3.23v2.06a6.51 6.51 0 0 1 0 13.42v2.06A8.5 8.5 0 0 0 14 3.23z"/>'
        : '<path d="M16.5 12A4.5 4.5 0 0 0 14 8.36v1.29l2.48 2.48c.02-.38.02-.75 0-1.13zM19 12a6.5 6.5 0 0 1-.78 3.11l1.46 1.46A8.47 8.47 0 0 0 21 12c0-4.08-2.89-7.49-6.73-8.3v2.06A6.52 6.52 0 0 1 19 12zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.46 8.46 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4l-2.08 2.08L12 8.16V4z"/>'
    }
  }

  toggleAutoplay() {
    if (this.autoplayTimer) {
      this.stopAutoplay()
    } else {
      this.startAutoplay()
    }
  }

  startAutoplay() {
    if (!this.pageFlip) return
    this.autoplayTimer = setInterval(() => {
      if (!this.pageFlip) { this.stopAutoplay(); return }
      const current = this.pageFlip.getCurrentPageIndex()
      const total = this.pageFlip.getPageCount()
      if (current >= total - 1) {
        this.pageFlip.turnToPage(0)
      } else {
        this.pageFlip.flipNext('top')
      }
    }, 3000)
    if (this.hasAutoplayIconTarget) {
      this.autoplayIconTarget.innerHTML = '<path d="M6 6h12v12H6z"/>'
    }
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer)
      this.autoplayTimer = null
    }
    if (this.hasAutoplayIconTarget) {
      this.autoplayIconTarget.innerHTML = '<path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z"/>'
    }
  }

  toggleFullscreen() {
    const container = this.element
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {})
    } else {
      container.requestFullscreen().catch(() => {})
    }
  }

  share() {
    const url = window.location.href
    const title = document.title || 'Event Guide'

    if (navigator.share) {
      navigator.share({ title, url }).catch(() => {})
    } else {
      navigator.clipboard.writeText(url).then(() => {
        alert('Link copied to clipboard!')
      }).catch(() => {
        window.open(`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`, '_self')
      })
    }
  }

  download() {
    const url = this.pdfUrl()
    if (!url) return
    const a = document.createElement('a')
    a.href = url
    a.download = ''
    a.target = '_blank'
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  playFlipSound() {
    if (!this.soundEnabled || !this.bookReady) return
    try {
      if (!this.flipAudio) {
        this.flipAudio = new Audio('/page-flip.mp3')
        this.flipAudio.volume = 0.7
      }
      this.flipAudio.currentTime = 0
      this.flipAudio.play().catch(() => {})
    } catch (_) {
      // Audio not available
    }
  }

  pdfUrl() {
    const url = this.element.dataset.flipbookPdfUrl
    return typeof url === "string" && url.trim().length > 0 ? url : null
  }

  async loadPdf(pdfUrl) {
    const loadingTask = pdfjsLib.getDocument({
      url: pdfUrl,
      withCredentials: false,
      cMapPacked: true,
      useSystemFonts: true,
      useWorkerFetch: false,
      useWasm: false,
      isOffscreenCanvasSupported: false
    })
    return await loadingTask.promise
  }

  updateProgress(current, total) {
    const pct = Math.round((current / total) * 100)
    if (this.hasProgressTextTarget) this.progressTextTarget.textContent = `${pct}%`
    if (this.hasProgressRingTarget) {
      const circumference = 2 * Math.PI * 42 // r=42
      this.progressRingTarget.style.strokeDashoffset = circumference - (circumference * pct / 100)
    }
    if (this.hasCounterTarget) this.counterTarget.textContent = `${pct}%`
  }

  async renderPages(pdf, startPage, endPage, totalPages) {
    const total = totalPages || endPage
    const images = []
    for (let pageNumber = startPage; pageNumber <= endPage; pageNumber += 1) {
      if (!this.isConnected) return []

      const page = await pdf.getPage(pageNumber)
      const viewport = page.getViewport({ scale: 1 })
      const scale = Math.min(1.5, 1000 / viewport.width)
      const scaledViewport = page.getViewport({ scale })
      const canvas = document.createElement("canvas")
      const context = canvas.getContext("2d", { alpha: false })
      if (!context) continue

      canvas.width = Math.floor(scaledViewport.width)
      canvas.height = Math.floor(scaledViewport.height)
      await page.render({ canvasContext: context, viewport: scaledViewport }).promise
      images.push(canvas.toDataURL("image/jpeg", 0.85))

      this.updateProgress(pageNumber, total)
    }
    return images
  }

  initializeBook(pageImages) {
    const isNarrow = window.innerWidth < 1024
    let bookWidth, bookHeight

    if (isNarrow) {
      const containerWidth = this.bookTarget.parentElement?.clientWidth || window.innerWidth
      bookWidth = Math.min(Math.floor(containerWidth - 16), 550)
      bookHeight = Math.round(bookWidth * 1.333)
    } else {
      bookWidth = 550
      bookHeight = 733
    }

    this.bookTarget.classList.remove("hidden")
    this.bookTarget.innerHTML = ""
    this.bookTarget.style.height = `${bookHeight}px`
    this.bookTarget.style.margin = "0 auto"
    this.bookTarget.style.maxWidth = `${bookWidth}px`
    this.bookTarget.style.width = "100%"

    this.pageFlip = new StPageFlip(this.bookTarget, {
      width: bookWidth,
      height: bookHeight,
      size: "fixed",
      showCover: true,
      mobileScrollSupport: isNarrow,
      useMouseEvents: !isNarrow,
      maxShadowOpacity: isNarrow ? 0.2 : 0.55,
      flippingTime: isNarrow ? 500 : 800,
      disableFlipByClick: false,
      usePortrait: true,
      startZIndex: 0,
      autoSize: false
    })

    this.pageFlip.on("init", () => {
      this.showBook()
      this.updateCounter()
    })
    this.pageFlip.on("flip", () => {
      this.playFlipSound()
      this.updateCounter()
    })
    this.pageFlip.on("changeOrientation", () => {
      this.pageFlip.update()
      this.updateCounter()
    })

    this.pageFlip.loadFromImages(pageImages)
  }

  setZoom(level) {
    this.zoomLevel = Math.max(0.8, Math.min(1.35, Number(level.toFixed(2))))
    if (this.hasBookTarget) {
      this.bookTarget.style.transform = `scale(${this.zoomLevel})`
      this.bookTarget.style.transformOrigin = "center center"
    }
  }

  updateCounter() {
    if (!this.pageFlip || !this.hasCounterTarget) return
    this.counterTarget.textContent = `${this.pageFlip.getCurrentPageIndex() + 1}/${this.pageFlip.getPageCount()}`
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
      this.loadingTarget.classList.add("flex")
    }
    if (this.hasBookTarget) this.bookTarget.classList.add("hidden")
    if (this.hasFallbackTarget) this.fallbackTarget.classList.add("hidden")
    if (this.hasCounterTarget) this.counterTarget.textContent = "..."
  }

  showBook() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
      this.loadingTarget.classList.remove("flex")
    }
    if (this.hasFallbackTarget) this.fallbackTarget.classList.add("hidden")
    if (this.hasBookTarget) this.bookTarget.classList.remove("hidden")
  }

  showFallback() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
      this.loadingTarget.classList.remove("flex")
    }
    if (this.hasBookTarget) this.bookTarget.classList.add("hidden")
    if (this.hasFallbackTarget) this.fallbackTarget.classList.remove("hidden")
    if (this.hasCounterTarget) this.counterTarget.textContent = "PDF"
    this.fallbackPage = 1
  }

  flipFallbackPage(direction) {
    if (!this.hasFallbackTarget) return
    const frame = this.fallbackTarget.querySelector("iframe")
    if (!frame) return
    this.fallbackPage = Math.max(1, (this.fallbackPage || 1) + direction)
    const baseUrl = this.pdfUrl()
    if (!baseUrl) return
    const separator = baseUrl.includes("#") ? "" : "#"
    frame.src = `${baseUrl}${separator}page=${this.fallbackPage}`
    if (this.hasCounterTarget) this.counterTarget.textContent = `${this.fallbackPage}`
  }
}
